import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  LocalShipping,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CustomPagination from "../../common/custom-pagination";
import { useAuth } from "../../hooks/AuthProvider";
import {
  LIST_CREATE_CLIMATRONICS_ORDERS,
  EDIT_CLIMATRONICS_ORDERS,
  LIST_CREATE_CLIMATRONICS_CUSTOMERS,
} from "../../config/urls";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import typography from "../../cssUtils/typography";
import AppChip from "../../common/app-chip";
import { ColorsConstants } from "../../cssUtils/constants";
import AppTable from "../../common/app-table";
import { formatWithTzLabel } from "../../utils/dateUtils";

const COLUMNS = [
  { label: "Order ID", id: "id" },
  { label: "Shipment ID", id: "shipment_id" },
  { label: "Customer", id: "customer" },
  { label: "Courier", id: "courier" },
  { label: "Status", id: "shipment_status" },
  { label: "Date", id: "date" },
  { label: "LR Numbers", id: "lr_numbers" },
  { label: "Updated At", id: "updated_at" },
  { label: "Updated By", id: "updated_by" },
  { label: "Actions", id: "actions" },
];

const SHIPMENT_STATUSES = [
  { name: "MANIFESTED", color: "info", courier: "Delhivery" },
  { name: "PICKED_UP", color: "primary", courier: "Delhivery" },
  { name: "LEFT_ORIGIN", color: "primary", courier: "Delhivery" },
  { name: "REACH_DESTINATION", color: "primary", courier: "Delhivery" },
  { name: "UNDEL_REATTEMPT", color: "warning", courier: "Delhivery" },
  { name: "PART_DEL", color: "success", courier: "Delhivery" },
  { name: "DELIVERED", color: "success", courier: "Delhivery" },
  { name: "RETURNED_INTRANSIT", color: "error", courier: "Delhivery" },
  { name: "RECEIVED_AT_RETURN_CENTER", color: "error", courier: "Delhivery" },
  { name: "RETURN_OFD", color: "error", courier: "Delhivery" },
  { name: "RETURN_DELIVERED", color: "error", courier: "Delhivery" },
  { name: "NOT_PICKED", color: "error", courier: "Delhivery" },
  { name: "LOST", color: "error", courier: "Delhivery" },

  { name: "BK", color: "info", courier: "Blue Dart" },
  { name: "PU", color: "primary", courier: "Blue Dart" },
  { name: "IT", color: "primary", courier: "Blue Dart" },
  { name: "AR", color: "primary", courier: "Blue Dart" },
  { name: "OD", color: "primary", courier: "Blue Dart" },
  { name: "DL", color: "success", courier: "Blue Dart" },
  { name: "UD", color: "warning", courier: "Blue Dart" },
  { name: "DA", color: "warning", courier: "Blue Dart" },
  { name: "NA", color: "warning", courier: "Blue Dart" },
  { name: "AD", color: "warning", courier: "Blue Dart" },
  { name: "RT", color: "error", courier: "Blue Dart" },
  { name: "RIT", color: "error", courier: "Blue Dart" },
  { name: "RDL", color: "error", courier: "Blue Dart" },
  { name: "EX", color: "error", courier: "Blue Dart" },
  { name: "OH", color: "warning", courier: "Blue Dart" },
];

const COURIER_OPTIONS = ["Delhivery", "Blue Dart", "Other"];

const COURIER_COLORS = {
  Delhivery: "warning",
  "Blue Dart": "info",
  Other: "default",
};

const EMPTY_FORM = {
  lr_number_1: "",
  lr_number_2: "",
  lr_number_3: "",
  courier: "",
  invoice_pdf_1: null,
  invoice_pdf_2: null,
  invoice_pdf_3: null,
  customer: "",
  order_id: "",
  shipment_id: "",
  shipment_status: "",
  shipment_date: "",
};

const OrderFormDialog = ({
  open,
  onClose,
  order,
  onSaved,
  getValidToken,
  tenant,
}) => {
  const user = localStorage.getItem("user");

  const isEdit = Boolean(order?.id);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (open) {
      if (order) {
        setForm({
          ...EMPTY_FORM,
          ...order,
          // customer arrives as a nested object on edit — send only the id
          customer: order.customer?.id ?? order.customer ?? "",
          // shipment_date arrives as a full ISO string — date input needs YYYY-MM-DD
          shipment_date: order.shipment_date
            ? order.shipment_date.slice(0, 10)
            : "",
          lr_number_1: order.lr_numbers?.[0] ?? "",
          lr_number_2: order.lr_numbers?.[1] ?? "",
          lr_number_3: order.lr_numbers?.[2] ?? "",
        });
      } else {
        setForm(EMPTY_FORM);
      }
      setErrors({});
    }
  }, [open, order]);

  const fetchCustomers = async () => {
    const token = await getValidToken();
    try {
      const response = await fetch(`${LIST_CREATE_CLIMATRONICS_CUSTOMERS}?registered=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenant,
        },
      });
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      setCustomers(data.results);
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.lr_number_1) errs.lr_number_1 = "LR Number is required";
    if (!form.invoice_pdf_1) errs.invoice_pdf_1 = "Invoice PDF is required";
    if (!form.customer) errs.customer = "Customer is required";
    if (!form.courier) errs.courier = "Courier is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setForm((prev) => ({ ...prev, [name]: files[0] }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const token = await getValidToken();
      const url = isEdit
        ? EDIT_CLIMATRONICS_ORDERS(order.id)
        : LIST_CREATE_CLIMATRONICS_ORDERS;

      // Rename customer to customer_id while submitting
      const body = {
        ...form,
        customer_id: form.customer,
        updated_by_id: JSON.parse(user).id,
      };
      delete body.customer;

      if (body.lr_numbers) {
        delete body.lr_numbers;
      }

      if (body.invoice_pdfs) {
        delete body.invoice_pdfs;
      }

      // Convert to formdata to send pdfs
      const formData = new FormData();
      Object.entries(body).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      const response = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenant,
        },
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to save order");
      const saved = await response.json();
      onSaved(saved, isEdit);
      onClose();
    } catch (err) {
      console.error("Error saving order:", err);
      setErrors(err.response?.data || { general: "Failed to save order" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Shipment Record</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {/* LR NUMBERS */}
          <Stack direction="row" spacing={2}>
            <Typography sx={{ ...typography.bodyLarge, fontWeight: "bold" }}>
              LR Numbers
            </Typography>
            <Box sx={{ flexGrow: 1, alignSelf: "center" }}>
              <Divider />
            </Box>
          </Stack>

          <TextField
            label="LR Number 1 *"
            name="lr_number_1"
            size="small"
            value={form.lr_number_1}
            onChange={handleChange}
            error={!!errors.lr_number_1}
            helperText={errors.lr_number_1}
            fullWidth
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="LR Number 2"
              name="lr_number_2"
              size="small"
              value={form.lr_number_2}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="LR Number 3"
              name="lr_number_3"
              size="small"
              value={form.lr_number_3}
              onChange={handleChange}
              fullWidth
            />
          </Stack>
          <TextField
            label="Courier *"
            name="courier"
            size="small"
            value={form.courier}
            onChange={handleChange}
            error={!!errors.courier}
            helperText={errors.courier}
            fullWidth
            select
          >
            {COURIER_OPTIONS.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>

          {/* INVOICE PDFs */}
          <Stack direction="row" spacing={2} pt={2}>
            <Typography sx={{ ...typography.bodyLarge, fontWeight: "bold" }}>
              Invoice PDFs
            </Typography>
            <Box sx={{ flexGrow: 1, alignSelf: "center" }}>
              <Divider />
            </Box>
          </Stack>

          <TextField
            label="Invoice PDF 1 *"
            fullWidth
            error={!!errors.invoice_pdf_1}
            helperText={errors.invoice_pdf_1}
            InputProps={{
              inputProps: {
                type: "file",
                accept: "application/pdf",
                onChange: handleFileChange,
                name: "invoice_pdf_1",
              },
            }}
            InputLabelProps={{ shrink: true }}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Invoice PDF 2"
              fullWidth
              InputProps={{
                inputProps: {
                  type: "file",
                  accept: "application/pdf",
                  onChange: handleFileChange,
                  name: "invoice_pdf_2",
                },
              }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Invoice PDF 3"
              fullWidth
              InputProps={{
                inputProps: {
                  type: "file",
                  accept: "application/pdf",
                  onChange: handleFileChange,
                  name: "invoice_pdf_3",
                },
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          {/* SHIPMENT INFO */}
          <Stack direction="row" spacing={2} pt={2}>
            <Typography sx={{ ...typography.bodyLarge, fontWeight: "bold" }}>
              Shipment Information
            </Typography>
            <Box sx={{ flexGrow: 1, alignSelf: "center" }}>
              <Divider />
            </Box>
          </Stack>

          <Autocomplete
            options={customers}
            getOptionLabel={(option) => option.name || ""}
            value={customers.find((c) => c.id === form.customer) || null}
            onChange={(e, newValue) => {
              setForm((prev) => ({
                ...prev,
                customer: newValue ? newValue.id : "",
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer *"
                size="small"
                error={!!errors.customer}
                helperText={errors.customer}
              />
            )}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Order ID"
              name="order_id"
              size="small"
              value={form.order_id}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Shipment ID"
              name="shipment_id"
              size="small"
              value={form.shipment_id}
              onChange={handleChange}
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <Autocomplete
              options={SHIPMENT_STATUSES}
              value={
                SHIPMENT_STATUSES.find(
                  (s) => s.name === form.shipment_status,
                ) || null
              }
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, newValue) => {
                setForm((prev) => ({
                  ...prev,
                  shipment_status: newValue ? newValue.name : "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Shipment Status"
                  size="small"
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <Stack
                  {...props}
                  key={option.name}
                  direction="row"
                  gap={0.5}
                  sx={{
                    alignItems: "flex-start !important",
                    textAlign: "left",
                    width: "100%",
                    justifyContent: "space-between !important",
                  }}
                >
                  <Typography sx={{ ...typography.bodyLarge }}>
                    {option.name}
                  </Typography>
                  <Typography
                    sx={{
                      ...typography.bodySmall,
                      color: ColorsConstants.primaryGray40,
                    }}
                  >
                    {option.courier}
                  </Typography>
                </Stack>
              )}
              fullWidth
            />

            <TextField
              type="date"
              label="Shipment Date"
              name="shipment_date"
              size="small"
              value={form.shipment_date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          {errors.general && <Alert severity="error">{errors.general}</Alert>}

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <ButtonTwo title="Cancel" onClick={onClose} />
            <ButtonOne
              title={isEdit ? "Update Shipment" : "Create Shipment"}
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              startIcon={
                saving ? <CircularProgress size={20} color="inherit" /> : null
              }
            />
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const Orders = () => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [searchText, setSearchText] = useState("");
  const searchTimerRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [courierFilter, setCourierFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [customers, setCustomers] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const tableColumns = useMemo(
    () => [
      {
        id: "order_id",
        label: "Order ID",
        render: (order) => (
          <Typography
            sx={{
              ...typography.bodyLarge,
              color: ColorsConstants.primaryBlue100,
              fontWeight: "bold",
              backgroundColor: ColorsConstants.primaryBlue70,
              borderRadius: 2,
              px: 2,
              py: 1,
              width: "fit-content",
            }}
          >
            {order.order_id || "N/A"}
          </Typography>
        ),
      },
      {
        id: "shipment_id",
        label: "Shipment ID",
        render: (order) => order.shipment_id || "N/A",
      },
      {
        id: "customer",
        label: "Customer",
        render: (order) => order.customer?.name ?? order.customer ?? "N/A",
      },
      {
        id: "courier",
        label: "Courier",
        render: (order) =>
          order.courier ? (
            <AppChip
              label={order.courier}
              color={COURIER_COLORS[order.courier]}
              variant="outlined"
              icon={<LocalShipping />}
            />
          ) : (
            "N/A"
          ),
      },
      {
        id: "shipment_status",
        label: "Status",
        render: (order) =>
          order.shipment_status ? (
            <AppChip
              label={order.shipment_status}
              color={
                SHIPMENT_STATUSES.find((s) => s.name === order.shipment_status)
                  ?.color || "default"
              }
              variant="outlined"
            />
          ) : (
            "N/A"
          ),
      },
      {
        id: "shipment_date",
        label: "Date",
        render: (order) =>
          order.shipment_date ? order.shipment_date.slice(0, 10) : "N/A",
      },
      {
        id: "lr_numbers",
        label: "LR Numbers",
        render: (order) => (
          <Stack direction="row" spacing={1}>
            {order.lr_numbers?.length
              ? order.lr_numbers.map((lr) => <AppChip key={lr} label={lr} />)
              : "N/A"}
          </Stack>
        ),
      },
      {
        id: "updated_at",
        label: "Updated At",
        render: (order) => formatWithTzLabel(order.updated_at) || "N/A",
      },
      {
        id: "updated_by",
        label: "Updated By",
        render: (order) => order.updated_by?.name || "N/A",
      },
      {
        id: "__actions__",
        label: "Actions",
        align: "center",
        render: (order) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(order);
            }}
            sx={{ color: "primary.main" }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [],
  );

  const fetchOrders = useCallback(
    async (search = searchText, currentPage = page, perPage = rowsPerPage) => {
      const token = await getValidToken();
      setLoading(true);
      try {
       const params = new URLSearchParams({
        offset: currentPage * perPage,
        limit: perPage,
      });
      if (search.trim()) {
        params.set("search", search.trim());
      }
      if (statusFilter) {
        params.set("shipment_status", statusFilter);
      }
      if (courierFilter) {
        params.set("courier", courierFilter);
      }
      if (customerFilter) {
        params.set("customer", customerFilter);
      }
      if (dateFilter) {
        params.set("shipment_date", dateFilter);
      }

        const response = await fetch(
          `${LIST_CREATE_CLIMATRONICS_ORDERS}?${params}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Tenant-ID": tenant,
            },
          },
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        setOrders(data.results);
        setTotalCount(data.count || 0);
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [getValidToken, tenant, searchText, page, rowsPerPage, statusFilter, courierFilter, customerFilter, dateFilter,],
  );

  useEffect(() => {
    fetchOrders(searchText, page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);
 useEffect(() => {
  setPage(0);
  fetchOrders(searchText, 0, rowsPerPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  statusFilter,
  courierFilter,
  customerFilter,
  dateFilter,
]);
useEffect(() => {
  fetchCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const fetchCustomers = async () => {
  const token = await getValidToken();

  try {
    const response = await fetch(
      `${LIST_CREATE_CLIMATRONICS_CUSTOMERS}?registered=true`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenant,
        },
      }
    );

    if (!response.ok) throw new Error("Failed to fetch customers");

    const data = await response.json();
    setCustomers(data.results);
  } catch (err) {
    console.error("Error fetching customers:", err);
  }
};
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
      fetchOrders(value, 0, rowsPerPage);
    }, 400);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    const perPage = parseInt(event.target.value, 10);
    setRowsPerPage(perPage);
    setPage(0);
  };

  const openCreate = () => {
    setSelectedOrder(null);
    setDialogOpen(true);
  };

  const openEdit = (order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  };

  const handleSaved = (saved, isEdit) => {
    if (isEdit) {
      setOrders((prev) => prev.map((o) => (o.id === saved.id ? saved : o)));
    } else {
      fetchOrders(searchText, 0, rowsPerPage);
      setPage(0);
    }
  };

  return (
    <Stack sx={{ p: 4 }}>
      {/* Toolbar */}
      {/*
        FIX: toolbar wasn't wrapping cleanly on smaller / mid-size viewports and the
        "Add Shipment" button would sometimes get squeezed against the filters.
        - Added flexWrap so items wrap to a second line instead of overflowing/squishing.
        - Added rowGap so wrapped rows keep consistent vertical spacing.
        - Switched justifyContent to flex-start and pinned "Add Shipment" to the end
          with marginLeft: "auto" (only when not wrapped) so layout stays balanced.
        - Gave each filter control flexShrink: 0 so they don't collapse when the
          search bar takes up its fixed width.
      */}
      <Stack
         direction={{ xs: "column", sm: "row" }}
         spacing={2}
         rowGap={2}
         alignItems={{ xs: "stretch", sm: "center" }}
         justifyContent="flex-start"
         flexWrap="wrap"
         sx={{ mb: 2 }}
      >
        <TextField
        placeholder="Search by Shipment ID, Customer, Courier, Status, or LR Number"
        value={searchText}
        onChange={handleSearchChange}
        size="small"
        sx={{
          // FIX: search bar widened further — now ~700px on large screens,
          // and stays full-width on small screens.
          width: {
            xs: "100%",
            sm: "100%",
            md: 600,
            lg: 700,
          },
          maxWidth: 700,
          flexShrink: 0,
        }}
        InputProps={{
          startAdornment: (
          <InputAdornment position="start">
            <SearchIcon
            fontSize="small"
            sx={{ color: "text.secondary" }}
            />
            </InputAdornment>
            ),
            }}
            />
            <Autocomplete
  size="small"
  options={COURIER_OPTIONS}
  value={courierFilter || null}
  onChange={(e, value) => {
    setCourierFilter(value || "");
    setStatusFilter("");
    setPage(0);
  }}
  sx={{ minWidth: 180, flexShrink: 0 }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Courier"
    />
  )}
/>
            <Autocomplete
  size="small"
   disabled={!courierFilter}
options={
  courierFilter
    ? SHIPMENT_STATUSES.filter(
        (status) => status.courier === courierFilter
      )
    : SHIPMENT_STATUSES
}  getOptionLabel={(option) => option.name}
  value={
    SHIPMENT_STATUSES.find(
      (s) => s.name === statusFilter
    ) || null
  }
  onChange={(e, value) => {
    const selectedStatus = value ? value.name : "";
    setStatusFilter(selectedStatus);
    setPage(0);
  }}
  sx={{ minWidth: 220, flexShrink: 0 }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Shipment Status"
    />
  )}
/>
<Autocomplete
  size="small"
  options={customers}
  getOptionLabel={(option) => option.name || ""}
  value={customers.find((c) => c.id === customerFilter) || null}
  onChange={(e, value) => {
    setCustomerFilter(value ? value.id : "");
    setPage(0);
  }}
  sx={{ minWidth: 220, flexShrink: 0 }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Customer"
    />
  )}
/>
<TextField
  type="date"
  size="small"
  label="Shipment Date"
  value={dateFilter}
  onChange={(e) => {
    setDateFilter(e.target.value);
    setPage(0);
  }}
  InputLabelProps={{ shrink: true }}
  sx={{ minWidth: 180, flexShrink: 0 }}
/>
<ButtonTwo
  title="Reset"
  onClick={() => {
    setSearchText("");
    setCourierFilter("");
    setStatusFilter("");
    setCustomerFilter("");
    setDateFilter("");
    setPage(0);

    fetchOrders("", 0, rowsPerPage);
  }}
/>

        <ButtonOne
          title="Add Shipment"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            whiteSpace: "nowrap",
            flexShrink: 0,
            ml: { xs: 0, sm: "auto" },
          }}
        />
      </Stack>

      <AppTable
        columns={tableColumns}
        rows={orders}
        rowKey="id"
        loading={loading}
        emptyMessage="No shipment records found"
        maxHeight={600}
        rowClickable={false}
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <OrderFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        order={selectedOrder}
        onSaved={handleSaved}
        getValidToken={getValidToken}
        tenant={tenant}
      />
    </Stack>
  );
};

export default Orders;